import React from 'react';
import { styled, SxProps, Theme } from '@mui/material/styles';

type CheckboxColor = 'primary' | 'error' | 'success';
type CheckboxSize = 'small' | 'medium';
type CheckboxState = 'unchecked' | 'checked' | 'indeterminate';

interface CheckboxInputProps extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'checked' | 'defaultChecked' | 'disabled' | 'onChange' | 'size' | 'type'
> {}

export interface CheckboxProps extends Omit<
    React.HTMLAttributes<HTMLSpanElement>,
    'color' | 'onChange' | 'onClick'
> {
    checked?: boolean;
    indeterminate?: boolean;
    disabled?: boolean;
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => void;
    color?: CheckboxColor;
    size?: CheckboxSize;
    white?: boolean;
    inputProps?: CheckboxInputProps;
    sx?: SxProps<Theme>;
}

const CONTROL_SIZE: Record<CheckboxSize, number> = {
    small: 20,
    medium: 24,
};

// The 2px border leaves 16x16px and 20x20px of inner space in the
// small and medium controls, respectively.
const CHECKMARK_SIZE: Record<CheckboxSize, { width: number; height: number }> =
    {
        small: { width: 14, height: 12 },
        medium: { width: 16, height: 14 },
    };

const Root = styled('span', {
    shouldForwardProp: prop =>
        !['$color', '$disabled', '$size', '$state', 'sx'].includes(
            prop as string
        ),
})<{
    $color: CheckboxColor;
    $disabled: boolean;
    $size: CheckboxSize;
    $state: CheckboxState;
}>(({ theme, $color, $disabled, $size, $state }) => {
    const isActive = $state !== 'unchecked';
    const palette = theme.palette[$color];

    return {
        'position': 'relative',
        'display': 'inline-flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        'flexShrink': 0,
        'lineHeight': 0,
        'verticalAlign': 'middle',
        'cursor': $disabled ? 'not-allowed' : 'pointer',
        'userSelect': 'none',
        '&:hover .Checkbox-visual': $disabled
            ? undefined
            : {
                  borderColor: isActive ? palette.dark : palette.main,
                  ...(isActive && {
                      backgroundColor: palette.dark,
                  }),
              },
        '& .Checkbox-input:focus-visible + .Checkbox-visual': {
            outline: `2px solid ${palette[200]}`,
            outlineOffset: 2,
        },
        '& .Checkbox-input:active + .Checkbox-visual': $disabled
            ? undefined
            : {
                  filter: 'brightness(0.9)',
              },
        '& .Checkbox-visual': {
            width: CONTROL_SIZE[$size],
            height: CONTROL_SIZE[$size],
        },
    };
});

const NativeInput = styled('input')({
    'position': 'absolute',
    'inset': 0,
    'zIndex': 1,
    'width': '100%',
    'height': '100%',
    'margin': 0,
    'padding': 0,
    'opacity': 0,
    'cursor': 'inherit',
    '&:disabled': {
        pointerEvents: 'none',
    },
});

const Visual = styled('span', {
    shouldForwardProp: prop =>
        !['$color', '$disabled', '$state', '$white'].includes(prop as string),
})<{
    $color: CheckboxColor;
    $disabled: boolean;
    $state: CheckboxState;
    $white: boolean;
}>(({ theme, $color, $disabled, $state, $white }) => {
    const isActive = $state !== 'unchecked';
    const activeColor = $disabled
        ? theme.palette.grey[400]
        : theme.palette[$color].main;

    return {
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        color: theme.palette.common.white,
        backgroundColor: isActive
            ? activeColor
            : $disabled
              ? theme.palette.grey[100]
              : $white
                ? theme.palette.common.white
                : 'transparent',
        border: `2px solid ${
            isActive
                ? activeColor
                : $disabled
                  ? theme.palette.grey[300]
                  : theme.palette.grey[500]
        }`,
        borderRadius: '30%',
        transition:
            'background-color 120ms ease, border-color 120ms ease, filter 80ms ease',
        pointerEvents: 'none',
    };
});

const Checkmark = styled('svg', {
    shouldForwardProp: prop => prop !== '$size',
})<{ $size: CheckboxSize }>(({ $size }) => ({
    display: 'block',
    flex: '0 0 auto',
    width: CHECKMARK_SIZE[$size].width,
    height: CHECKMARK_SIZE[$size].height,
    overflow: 'visible',
}));

const Dash = styled('span')({
    width: '60%',
    height: 2,
    borderRadius: 999,
    backgroundColor: 'currentColor',
});

export const Checkbox = React.forwardRef<HTMLSpanElement, CheckboxProps>(
    function Checkbox(
        {
            checked = false,
            indeterminate = false,
            disabled = false,
            color = 'primary',
            size = 'small',
            white = true,
            inputProps,
            onChange,
            sx,
            ...rootProps
        },
        ref
    ) {
        const inputRef = React.useRef<HTMLInputElement>(null);
        const state: CheckboxState = indeterminate
            ? 'indeterminate'
            : checked
              ? 'checked'
              : 'unchecked';

        React.useEffect(() => {
            if (inputRef.current) {
                inputRef.current.indeterminate = indeterminate;
            }
        }, [indeterminate]);

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            onChange?.(event, event.target.checked);

            if (inputRef.current) {
                inputRef.current.indeterminate = indeterminate;
            }
        };

        return (
            <Root
                {...rootProps}
                ref={ref}
                $color={color}
                $disabled={disabled}
                $size={size}
                $state={state}
                sx={sx}
                onClick={event => event.stopPropagation()}>
                <NativeInput
                    {...inputProps}
                    ref={inputRef}
                    className='Checkbox-input'
                    type='checkbox'
                    checked={checked}
                    disabled={disabled}
                    readOnly={!onChange || inputProps?.readOnly}
                    aria-checked={indeterminate ? 'mixed' : checked}
                    onChange={handleChange}
                />
                <Visual
                    aria-hidden='true'
                    className='Checkbox-visual'
                    $color={color}
                    $disabled={disabled}
                    $state={state}
                    $white={white}>
                    {state === 'checked' && (
                        <Checkmark
                            aria-hidden='true'
                            focusable='false'
                            $size={size}
                            viewBox='0 0 16 14'
                            fill='none'>
                            <path
                                d='M1.5 7 6 11.5 14.5 2.5'
                                stroke='currentColor'
                                strokeWidth='2.25'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                            />
                        </Checkmark>
                    )}
                    {state === 'indeterminate' && <Dash />}
                </Visual>
            </Root>
        );
    }
);

export default Checkbox;
